// Modules
import { useEffect, useState, useReducer } from 'react';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

// Components
import { StyledTitle, FilterRowWrapper, ButtonPrimary } from '../../common/components';
import { AdminDocumentRepositoryGrid } from './AdminDocumentRepositoryGrid';
import { AdminDocumentRepositoryWrapper } from './AdminDocumentRepositoryStyles';

// Utils
import { gridReducer, GRID_ACTIONS, IGridReducerState } from '../../reducer/gridReducer/gridReducer';
import moment from 'moment';
import { REVERSED_DATE_FORMAT_HYPHEN } from '../../common/constants';

// Modals
import { BlankModal } from '../mymessages/modalTemplates/BlankModal';
import { ConfirmationModal } from '../mymessages/modalTemplates/ConfirmationModal';
import { AdminDocumentRepositoryModal } from './AdminDocumentRepositoryModal';
import { AdminBulkUploadModal } from './AdminBulkUploadModal';
import { AdminBulkUploadFailureModal } from './AdminBulkUploadFailureModal';

import {
  createDocument,
  deleteDocument,
  getAdminDocuments,
  publishDocument,
  updateDocument,
  createBulkUpload,
} from '../../common/api/adminDocumentsApi';
import { globalApiParams } from '../../common/api/globals';

export const AdminDocumentRepository = () => {
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState<boolean>(false);
  const [showBulkUploadFailureModal, setShowBulkUploadFailureModal] = useState<boolean>(false);
  const [showPublishDocumentModal, setShowPublishDocumentModal] = useState<boolean>(false);
  const [showDocumentExpiredModal, setShowDocumentExpiredModal] = useState<boolean>(false);
  const [showIsPublishedDocumentModal, setShowIsPublishedDocumentModal] = useState<boolean>(false);
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState<boolean>(false);
  const [showIsDeletedDocumentModal, setShowIsDeletedDocumentModal] = useState<boolean>(false);
  const [isPublishEnabled, setIsPublishEnabled] = useState<boolean>(false);
  const [failureMessages, setFailureMessage] = useState<Array<string>>();
  const [missingFiles, setMissingFiles] = useState<Array<Api.IMissingFile>>();

  const { instance, accounts } = useMsal();
  const username = accounts[0] && accounts[0].username;

  const initialState: IGridReducerState = {
    page: 1,
    currentType: '',
    data: {},
    gridData: [],
    initialSortField: 'startDate',
    initialSortOrder: 'DESC',
    currentSortField: 'startDate',
    currentSortOrder: 'DESC',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: '-1',
  };

  const [state, dispatch] = useReducer(gridReducer, initialState);

  const error = (error: string) => {
    const message = `API error calling: ${error}`;
    console.error(message);
    toast.error(message, { position: 'bottom-right' });
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
  };

  const adminMessagesSuccess = (result: Common.IResult<Array<Api.IAdminMessagesPagedData>>) => {
    if (result && result.success) {
      dispatch({
        type: GRID_ACTIONS.ADD_DATA,
        payload: { data: result.data, page: state.page },
      });
    } else {
      error('getAdminDocuments');
    }

    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
  };

  const loadData = async () => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const params: Api.IAdminMessagesGridParams = {
      sortField: state.currentSortField,
      sortDir: state.currentSortOrder,
      page: state.page,
      pageSize: globalApiParams.pageSize,
    };

    getAdminDocuments(accounts[0], instance, params, adminMessagesSuccess, error);
  };

  useEffect(() => {
    loadData();
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentSortField, state.currentSortOrder, state.page]);

  const handleSortChange = (sort: Common.ISort) => {
    setSelectedRowIndex(-1);
    dispatch({ type: GRID_ACTIONS.CHANGE_SORT, payload: sort });
  };

  const handlePagingChange = () => {
    dispatch({ type: GRID_ACTIONS.INCREMENT_PAGE });
  };

  const handleSelectedRowChange = (index: number, item: any) => {
    setSelectedRowIndex(index);
    setSelectedRow(item);
  };

  const deleteDocumentSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setShowIsDeletedDocumentModal(true);
    } else {
      error('deletedocument');
    }
  };

  const handleDelete = (row: any) => {
    setSelectedRowIndex(-1);
    setShowDeleteDocumentModal(true);
  };

  const handleDeleteDocumentYes = () => {
    setShowDeleteDocumentModal(false);

    const row = selectedRow;

    if (row) {
      deleteDocument(accounts[0], instance, row.documentId, deleteDocumentSuccess, error);
    }
  };

  const handleNewDocument = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setSelectedRowIndex(-1);
    setShowAddNewModal(true);
  };

  const handleBulkUpload = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setSelectedRowIndex(-1);
    setShowBulkUploadModal(true);
  };

  const handleCreateClick = async (document: any) => {
    await createDocument(accounts[0], document, instance, createDocumentSuccess, error);
  };

  const handleBulkUploadCreateClick = async (request: any) => {
    await createBulkUpload(accounts[0], request, instance, bulkUploadSuccess, error);
  };

  const bulkUploadSuccess = (result: Api.IBulkUploadResponse) => {
    if (result) {
      if (result.success) {
        // Reload grid
        loadData();
        dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
        setSelectedRowIndex(-1);
      } else if (!result.success && result.errors && result.errors?.length > 0) {
        setFailureMessage(result.errors);
        setMissingFiles(result.data);
        setShowBulkUploadFailureModal(true);
      }
    } else {
      error('createBulkUpload');
    }
  };

  const createDocumentSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setSelectedRowIndex(-1);
    } else {
      error('createmessage');
    }
  };

  const handleUpdateClick = async (document: any) => {
    await updateDocument(accounts[0], document, instance, updateDocumentSuccess, error);
  };

  const updateDocumentSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setSelectedRowIndex(-1);
    } else {
      error('updatemessage');
    }
  };

  const publishDocumentSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setShowIsPublishedDocumentModal(true);
    } else {
      error('publishmessage');
    }
  };

  const handlePublishDocumentYes = () => {
    setShowPublishDocumentModal(false);

    const row = selectedRow;
    if (row) {
      setIsPublishEnabled(false);
      publishDocument(accounts[0], instance, row.documentId, publishDocumentSuccess, error);
    }
  };

  const handlePublish = (event: any) => {
    const currentDate = moment().format(REVERSED_DATE_FORMAT_HYPHEN);
    const row = selectedRow;
    const endDate = row['endDate'];

    if (endDate <= currentDate) {
      setShowDocumentExpiredModal(true);
    } else {
      setShowPublishDocumentModal(true);
    }
  };

  const handleDownloadGenericDocument = async (id: number) => {
    const request = {
      ...loginRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(request)
      .then(async (response: any) => {
        var headers = new Headers();
        var bearer = 'Bearer ' + response.idToken;
        headers.append('Authorization', bearer);
        var options = {
          method: 'GET',
          headers: headers,
          responseType: 'blob',
        };

        const url = process.env.REACT_APP_API_SERVICES_URL;
        const downloadGenericDocumentAdminEndpoint = `${url}/documentmanagement/getdocument/${id}`;

        fetch(downloadGenericDocumentAdminEndpoint, options).then(async response => {
          if (response.status === 200) {
            let contentDisposition = response.headers.get('content-disposition');
            let fileName = contentDisposition?.split('filename=')[1].split(';')[0];
            // remove outer quotes
            fileName = fileName?.replace(/"/g, '');

            const downloadUrl = window.URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName!);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        });
      })
      .catch((e: any) => {
        const message = 'API error calling: downloadGenericDocumentAdmin';
        console.error(message);
        toast.error(message, { position: 'bottom-right' });
      });
  };

  useEffect(() => {
    const enabledPublish =
      selectedRowIndex >= 0 &&
      (selectedRow.status === 'Ready to publish' || selectedRow.status === 'Expired') &&
      selectedRow.updatedBy !== username;

    setIsPublishEnabled(enabledPublish);
  }, [selectedRow]);

  const isUpdateEnabled =
    selectedRowIndex >= 0 && selectedRow.status !== 'Published' && selectedRow.status !== 'Expired';

  const selectedItem: Api.IDocumentRepositoryRequest = {
    file: selectedRow >= 0 ? selectedRow?.file : null,
    format: selectedRowIndex >= 0 ? selectedRow?.format : '',
    fileName: selectedRowIndex >= 0 ? selectedRow?.fileName : '',
    title: selectedRowIndex >= 0 ? selectedRow?.title : '',
    type: selectedRowIndex >= 0 ? selectedRow?.type : '',
    audience: selectedRowIndex >= 0 ? selectedRow?.audience : '',
    notifyByEmail: selectedRowIndex >= 0 ? selectedRow?.notifyByEmail : false,
    notifyByAlert: selectedRowIndex >= 0 ? selectedRow?.notifyByAlert : false,
    startDate: selectedRowIndex >= 0 ? selectedRow?.startDate : '',
    endDate: selectedRowIndex >= 0 ? selectedRow?.endDate : '',
    status: selectedRowIndex >= 0 ? selectedRow?.status : '',
    references: selectedRowIndex >= 0 ? selectedRow?.references : [],
    bucket: selectedRowIndex >= 0 ? selectedRow?.bucket : '',
    storageKey: selectedRowIndex >= 0 ? selectedRow?.storageKey : '',
    id: selectedRowIndex >= 0 ? selectedRow?.documentId : '',
  };

  return (
    <AdminDocumentRepositoryWrapper>
      <div className='adminDocumentRepository container-fluid'>
        <div className='row'>
          <div className='col-md-12'>
            <FilterRowWrapper>
              <StyledTitle>Document repository upload</StyledTitle>
              <div className='d-flex justify-content-end'>
                <ButtonPrimary className='mr-3' title='Bulk upload' onClick={handleBulkUpload} />
                <ButtonPrimary title='New document' onClick={handleNewDocument} />
                <ButtonPrimary
                  className='ml-3'
                  title='Update'
                  onClick={() => setShowEditModal(true)}
                  disabled={!isUpdateEnabled}
                />
                <ButtonPrimary className='ml-3' title='Publish' onClick={handlePublish} disabled={!isPublishEnabled} />
              </div>
            </FilterRowWrapper>
          </div>
        </div>
      </div>

      <AdminDocumentRepositoryGrid
        gridData={state.gridData}
        isLoading={state.isLoading}
        hasMoreData={state.hasMoreData}
        selectedRowIndex={selectedRowIndex}
        handleSortChange={handleSortChange}
        handlePagingChange={handlePagingChange}
        handleSelectedRowChange={handleSelectedRowChange}
        handleDelete={handleDelete}
        currentSortField={state.currentSortField}
        currentSortOrder={state.currentSortOrder}
      />

      <AdminDocumentRepositoryModal
        show={showAddNewModal}
        edit={false}
        handleDownloadGenericDocument={handleDownloadGenericDocument}
        onSubmit={(isAccepted: boolean, item: Api.IDocumentRepositoryRequest) => {
          setShowAddNewModal(false);
          if (isAccepted) {
            var newItem: Api.IDocumentRepositoryRequest = {
              file: item.file,
              format: item.format,
              fileName: item.fileName,
              title: item.title,
              type: item.type,
              audience: item.audience,
              notifyByEmail: item.notifyByEmail,
              notifyByAlert: item.notifyByAlert ? item.notifyByAlert : false,
              startDate: item.startDate,
              endDate: item.endDate,
              status: item.status,
              references: item.references,
            };

            handleCreateClick(newItem);
          }
        }}
        onClose={() => {
          setShowAddNewModal(false);
        }}
      ></AdminDocumentRepositoryModal>

      {showEditModal && (
        <AdminDocumentRepositoryModal
          show={showEditModal}
          edit={true}
          handleDownloadGenericDocument={handleDownloadGenericDocument}
          onSubmit={(isAccepted: boolean, item: Api.IDocumentRepositoryRequest) => {
            setShowEditModal(false);
            if (isAccepted) {
              var newItem: Api.IDocumentRepositoryRequest = {
                file: item.file,
                format: item.format,
                fileName: item.fileName,
                title: item.title,
                type: item.type,
                audience: item.audience,
                notifyByEmail: item.notifyByEmail,
                notifyByAlert: item.notifyByAlert ? item.notifyByAlert : false,
                startDate: item.startDate,
                endDate: item.endDate,
                status: item.status,
                references: item.references,
                bucket: selectedRow.bucket,
                storageKey: selectedRow.storageKey,
                id: selectedRow.documentId,
              };

              handleUpdateClick(newItem);
            }
          }}
          onClose={() => {
            setShowEditModal(false);
          }}
          item={selectedItem}
        ></AdminDocumentRepositoryModal>
      )}

      {showBulkUploadModal && (
        <AdminBulkUploadModal
          show={showBulkUploadModal}
          onSubmit={(isAccepted: boolean, item: Api.IBulkUploadRequest) => {
            setShowBulkUploadModal(false);
            if (isAccepted) {
              var newItem: Api.IBulkUploadRequest = {
                orchestrationFile: item.orchestrationFile,
                parentDirectory: item.parentDirectory,
              };
              handleBulkUploadCreateClick(newItem);
            }
          }}
          onClose={() => {
            setShowBulkUploadModal(false);
          }}
        ></AdminBulkUploadModal>
      )}

      {showBulkUploadFailureModal && (
        <AdminBulkUploadFailureModal
          onClose={() => setShowBulkUploadFailureModal(false)}
          failureMessages={failureMessages}
          missingFiles={missingFiles}
        ></AdminBulkUploadFailureModal>
      )}

      <BlankModal
        show={showDocumentExpiredModal}
        onHide={() => {
          setShowDocumentExpiredModal(false);
        }}
        titleText={'This document has expired. '}
        bodyText={'Please update the end date and publish again.'}
        buttonText={'Ok'}
      ></BlankModal>

      <ConfirmationModal
        show={showPublishDocumentModal}
        onHide={() => {
          setShowPublishDocumentModal(false);
        }}
        onYes={() => {
          handlePublishDocumentYes();
        }}
        onNo={() => {
          setShowPublishDocumentModal(false);
        }}
        titleText={'Do you want to publish the document?'}
      ></ConfirmationModal>

      <BlankModal
        show={showIsPublishedDocumentModal}
        onHide={() => {
          setShowIsPublishedDocumentModal(false);
        }}
        titleText={'Document is successfully published in the portal.'}
        buttonText={'OK'}
      ></BlankModal>

      <ConfirmationModal
        show={showDeleteDocumentModal}
        onHide={() => {
          setShowDeleteDocumentModal(false);
        }}
        onYes={() => {
          handleDeleteDocumentYes();
        }}
        onNo={() => {
          setShowDeleteDocumentModal(false);
        }}
        titleText={'Do you want to delete the document?'}
      ></ConfirmationModal>

      <BlankModal
        show={showIsDeletedDocumentModal}
        onHide={() => {
          setShowIsDeletedDocumentModal(false);
        }}
        titleText={'Document is deleted from the admin page and the portal.'}
        buttonText={'OK'}
      ></BlankModal>
    </AdminDocumentRepositoryWrapper>
  );
};
